import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export type TooltipPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface TooltipProps {
  /** Tooltip content */
  content: React.ReactNode;
  /** Title displayed in the tooltip */
  title?: string;
  /** Position of the tooltip relative to the trigger element */
  position?: TooltipPosition;
  /** Children element that triggers the tooltip */
  children: React.ReactNode;
  /** Custom className for the tooltip container */
  className?: string;
  /** Disable tooltip (hide it) */
  disabled?: boolean;
}

const ARROW_SIZE = 8;
const TOOLTIP_OFFSET = 8;

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  title,
  position = 'top-center',
  children,
  className = '',
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  /* 消える動きを出しきってから外すための居残り。isVisible が false でも
     --tt-out-dur のあいだは DOM に残す */
  const [keepMounted, setKeepMounted] = useState(false);
  const unmountRef = useRef<NodeJS.Timeout | null>(null);
  /*
    最初から absolute で置いておく。**空のままだと最初の表示で幅を測ったとき、
    ふつうの段落として画面の幅いっぱいに広がった値が返る。** その幅で真ん中に
    寄せると、吹き出しが画面の左端へ飛び、矢印だけがボタンの上に残る
  */
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 9999,
  });
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tooltipWidth = tooltipEl?.offsetWidth || 0;
    const tooltipHeight = tooltipEl?.offsetHeight || 0;

    let top = 0;
    let left = 0;
    let arrowLeft = '50%';
    let arrowTop = 'auto';
    let arrowBottom = 'auto';
    let arrowTransform = 'translateX(-50%)';

    const isTop = position.startsWith('top');

    if (isTop) {
      top = triggerRect.top - tooltipHeight - TOOLTIP_OFFSET + window.scrollY;
      arrowTop = '100%';
    } else {
      top = triggerRect.bottom + TOOLTIP_OFFSET + window.scrollY;
      arrowBottom = '100%';
    }

    if (position.endsWith('center')) {
      left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
    } else if (position.endsWith('left')) {
      left = triggerRect.left;
      arrowLeft = '16px';
      arrowTransform = 'none';
    } else if (position.endsWith('right')) {
      left = triggerRect.right - tooltipWidth;
      arrowLeft = 'auto';
      arrowTransform = 'none';
    }

    // 画面の端で切れないよう、横は画面の内側に収める。矢印はずらした分だけ逆へ動かし、
    // ボタンの真ん中を指したままにする
    const viewportMargin = 8;
    const maxLeft = window.innerWidth - tooltipWidth - viewportMargin;
    const clampedLeft = Math.max(viewportMargin, Math.min(left, maxLeft));
    if (tooltipWidth > 0 && clampedLeft !== left) {
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      arrowLeft = `${Math.max(12, Math.min(tooltipWidth - 12, triggerCenter - clampedLeft))}px`;
      arrowTransform = 'translateX(-50%)';
      left = clampedLeft;
    }

    setTooltipStyle({
      position: 'absolute',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 9999,
    });

    setArrowStyle({
      position: 'absolute',
      left: position.endsWith('right') && arrowLeft === 'auto' ? 'auto' : arrowLeft,
      right: position.endsWith('right') && arrowLeft === 'auto' ? '16px' : 'auto',
      top: arrowTop,
      bottom: arrowBottom,
      transform: arrowTransform,
      width: 0,
      height: 0,
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      ...(isTop
        ? { borderTop: `${ARROW_SIZE}px solid #111827` }
        : { borderBottom: `${ARROW_SIZE}px solid #111827` }),
    });
  }, [position]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    /* 戻ってきたら居残りの取り消し。外す予約が生きたままだと、
       出した直後に消える */
    if (unmountRef.current) {
      clearTimeout(unmountRef.current);
      unmountRef.current = null;
    }
    setKeepMounted(true);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      /* --tt-out-dur は 50ms。少し余裕を見て 120ms 後に外す。
         ここで即座に外すと、消える動きが再生される前に要素が消える */
      unmountRef.current = setTimeout(() => setKeepMounted(false), 120);
    }, 100);
  };

  useEffect(() => {
    if (isVisible && !disabled) {
      calculatePosition();
    }
  }, [isVisible, disabled, calculatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (unmountRef.current) {
        clearTimeout(unmountRef.current);
      }
    };
  }, []);

  /*
  # 出るまでの間と、消えるときの間

  transitions.dev の tooltip を入れた。**出るのに 80ms 待ってから 150ms かけ、
  消えるのは 50ms で待ち時間なし。** 待ちがあるので、カーソルが通り過ぎる
  だけでは出ない。逆に消すほうを速くするのは、次の要素へ移ったときに前の
  吹き出しが残って見えるのを避けるため。

  ここは `isVisible` が false になった瞬間に portal ごと外れる作りだったので、
  **消えるほうの動きは再生される前に要素が消えていた。** 一度でも出したら
  外さずに残し、data-show の付け外しで見せ消しする形に変えた。
  外すのは 50ms 経ってから (handleMouseLeave 側)
  */
  const shouldMount =
    (isVisible || keepMounted) && !disabled && typeof document !== 'undefined';

  const tooltipContent = shouldMount ? createPortal(
    <div
      ref={tooltipRef}
      style={tooltipStyle}
      role="tooltip"
      className="t-tt-fade"
      data-show={isVisible ? 'true' : 'false'}
    >
      <div className="bg-gray-900 text-white rounded-lg px-3 py-1.5 shadow-lg max-w-xs">
        {title && (
          <div className="font-sans font-bold max-sm:text-[0.9375rem] text-xs leading-normal mb-0.5">
            {title}
          </div>
        )}
        <div className="font-sans font-normal max-sm:text-[0.9375rem] text-xs leading-normal whitespace-nowrap">
          {content}
        </div>
      </div>
      <div style={arrowStyle} />
    </div>,
    document.body
  ) : null;

  return (
    <div
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      /* 吹き出しはマウスのときだけ。指で押すと mouseenter だけが来て mouseleave が来ず、
         押したあとも吹き出しが残って隣の釦や提出の結果の欄にかかっていた (2026-10-29、390 幅) */
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") handleMouseEnter();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") handleMouseLeave();
      }}
      /* 押したら消す。マウスが釦の上に残ったままだと、提出のあとに出る結果の欄へ
         吹き出しがかぶり続けていた (2026-10-29、本番の 1440 幅) */
      onPointerDown={() => {
        if (isVisible) handleMouseLeave();
      }}
    >
      {children}
      {tooltipContent}
    </div>
  );
};
